<template>
  <div class="p-6 dark:text-white glass-card-lite">
    <!-- Header Section -->
    <div class="flex items-center gap-3 mb-8">
      <div class="bg-blue-500/20 p-2 rounded-lg">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-blue-500">
          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
        </svg>
      </div>
      <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
        API Key Management
      </h1>
    </div>

    <!-- API Key Header -->
    <div class="mb-4">
      <span class="text-pink-500 font-mono text-sm">X-API-Key</span>
    </div>

    <!-- API Key Section -->
    <div class="mb-6">
      <label class="block text-sm font-medium text-gray-400 mb-3">YOUR API KEY</label>
      
      <div class="flex gap-4 mb-4">
        <!-- API Key Input -->
        <div class="flex-1 relative">
          <input
            :type="showApiKey ? 'text' : 'password'"
            :value="apiKey"
            readonly
            class="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          
          <!-- Eye Icon -->
          <button
            @click="toggleApiKeyVisibility"
            class="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg v-if="!showApiKey" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
              <line x1="1" y1="1" x2="23" y2="23"></line>
            </svg>
          </button>

          <!-- Copy Icon -->
          <button
            @click="copyApiKey"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>
        </div>

        <!-- Regenerate Button -->
        <button
          @click="showRegenerateModal = true"
          class="flex items-center gap-2 btn bg-yellow-500 text-gray-900 hover:bg-yellow-600 transition-all font-medium"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="23 4 23 10 17 10"></polyline>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
          </svg>
          Regenerate Key
        </button>
      </div>

      <!-- Warning Message -->
      <div class="bg-yellow-900/30 rounded-md p-4 flex gap-3">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-yellow-500 flex-shrink-0 mt-0.5">
          <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
        <p class="dark:text-yellow-400 text-gray-700 text-sm">
          <span class="font-semibold">Warning:</span> Regenerating your API key will invalidate the old key immediately. Any applications using the old key will need to be updated.
        </p>
      </div>
    </div>

    <!-- Usage Example Section -->
    <div class="mt-8">
      <h2 class="text-lg font-semibold text-white mb-3">Usage Example</h2>
      <div class="bg-gray-900 border border-gray-700 rounded-md p-4">
        <pre class="text-sm text-gray-300 font-mono overflow-x-auto"><code>curl -H "X-API-Key: YOUR_API_KEY" https://your-server/api/devices</code></pre>
      </div>
    </div>

    <!-- Regenerate Confirmation Modal -->
    <div v-if="showRegenerateModal" class="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div class="bg-gray-800 rounded-lg p-6 max-w-md w-full">
        <div class="flex items-start gap-3 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-yellow-500 flex-shrink-0">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <div>
            <h2 class="text-xl font-bold text-white mb-2">Regenerate API Key?</h2>
            <p class="text-gray-300 text-sm">
              This action will immediately invalidate your current API key. All applications using the old key will stop working until updated with the new key.
            </p>
          </div>
        </div>

        <div class="flex gap-3 mt-6">
          <button
            @click="regenerateApiKey"
            class="flex-1 px-4 py-2 bg-yellow-500 text-gray-900 rounded-md hover:bg-yellow-600 transition-all font-medium"
          >
            Regenerate
          </button>
          <button
            @click="showRegenerateModal = false"
            class="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Success Toast -->
    <div
      v-if="showSuccessToast"
      class="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
      <span>{{ successMessage }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { getApiKey, regenerateKey } from '@/services/mivulite/apiKeyService';

const apiKey = ref('');
const showApiKey = ref(false);
const showRegenerateModal = ref(false);
const showSuccessToast = ref(false);
const successMessage = ref('');

const fetchApiKey = async () => {
  try {
    const data = await getApiKey();
    console.log(data)
    apiKey.value = data.api_key;
  } catch (error) {
    console.error('Error fetching API key:', error);
  }
};

const toggleApiKeyVisibility = () => {
  showApiKey.value = !showApiKey.value;
};

const copyApiKey = async () => {
  try {
    await navigator.clipboard.writeText(apiKey.value);
    showToast('API Key copied to clipboard!');
  } catch (error) {
    console.error('Error copying API key:', error);
  }
};

const regenerateApiKey = async () => {
  try {
    const data = await regenerateKey();
    apiKey.value = data.key;
    showRegenerateModal.value = false;
    showToast('API Key regenerated successfully!');
  } catch (error) {
    console.error('Error regenerating API key:', error);
  }
};

const showToast = (message) => {
  successMessage.value = message;
  showSuccessToast.value = true;
  setTimeout(() => {
    showSuccessToast.value = false;
  }, 3000);
};

onMounted(() => {
  fetchApiKey();
});
</script>